import React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { usePermissions } from '../../context/permissionContext';
import useInactivityTimer from '../../hooks/useInactivityTimer';
import InactivityModal from '../inactivity-modal';
import url from '../../config.js';
import { fullLogout } from '../../auth/oidc.js';
import './protectedroute.scss';

function SessionGuard({ children }) {
  const { logout } = usePermissions();
  const navigate = useNavigate();

  function handleLogout() {
    // Conversation state (smarthub/bf/migration) is intentionally NOT cleared here.
    // This logout is triggered by inactivity — the user didn't choose to start over.
    // State is keyed by userId so it restores automatically on next login for the same account.
    localStorage.removeItem('langraph_session_id');
    logout();
    // End the Keycloak SSO session too for Keycloak logins (else silent re-login).
    fullLogout();
  }

  async function handleRelogin(password) {
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    const res = await fetch(`${url.domain}/api/auth/signin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: storedUser.username, password }),
    });
    if (!res.ok) throw new Error('Invalid credentials');
    const data = await res.json();
    localStorage.setItem('token', data.accessToken);
    localStorage.setItem('user', JSON.stringify({
      id: data.id,
      username: data.username,
      email: data.email,
      roles: data.roles,
      permission: data.permission,
      tenantId: data.tenantId,
      pagePermissions: data.pagePermissions || {},
    }));
  }

  const { showWarning, secondsLeft, dismissWarning } = useInactivityTimer({
    timeoutMs: 30 * 60 * 1000,
    countdownMs: 60 * 1000,
    onLogout: handleLogout,
    enabled: true,
  });

  return (
    <React.Fragment>
      {children}
      <InactivityModal
        show={showWarning}
        secondsLeft={secondsLeft}
        onRelogin={handleRelogin}
        onStay={dismissWarning}
        onLogout={handleLogout}
      />
    </React.Fragment>
  );
}

const ProtectedRoute = ({ children, requiredPermission }) => {
  const { hasPermission } = usePermissions();

  const user = localStorage.getItem('user');
  if (!user || user === 'null') {
    return <Navigate to="/" replace />;
  }

  const permitted = !requiredPermission || (
    Array.isArray(requiredPermission)
      ? requiredPermission.some(p => hasPermission(p))
      : hasPermission(requiredPermission)
  );
  if (!permitted) {
    return (
      <div className="access-denied-container">
        <div className="access-denied-icon">🚫</div>
        <h2 className="access-denied-title">Access Denied</h2>
        <p className="access-denied-description">
          You don't have permission to access this page. Please contact your administrator if you believe this is an error.
        </p>
        <button
          className="access-denied-button"
          onClick={() => window.history.back()}
        >
          ← Go Back
        </button>
      </div>
    );
  }

  return <SessionGuard>{children}</SessionGuard>;
};

export default ProtectedRoute;